import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useQuoteContext } from '../../quoteContext';
import QuoteContactsDrawer, {
  QuoteContactsDrawerTab,
} from './QuoteContactsDrawer';

interface QuoteContactsDrawerContextTypes {
  /** Opens the quote contacts drawer, on the `contacts` tab by default. */
  openContactsDrawer: (tab?: QuoteContactsDrawerTab) => void;
}

const QuoteContactsDrawerContext =
  createContext<QuoteContactsDrawerContextTypes | null>(null);

export const useQuoteContactsDrawer = () => {
  const context = useContext(QuoteContactsDrawerContext);
  if (!context) {
    throw new Error(
      `useQuoteContactsDrawer must be rendered within the QuoteContactsDrawerProvider`,
    );
  }
  return context;
};

/**
 * Owns the contacts drawer and renders it, so anything on the quote page can
 * open it — on a specific tab — without an open flag being drilled down the
 * component tree.
 */
export const QuoteContactsDrawerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [openTab, setOpenTab] = useState<QuoteContactsDrawerTab | null>(null);
  const {
    quoteData: { quote, evaluateQuoteRules, updateQuoteSigningOrder },
  } = useQuoteContext();

  const openContactsDrawer = useCallback(
    (tab: QuoteContactsDrawerTab = 'contacts') => setOpenTab(tab),
    [],
  );

  const value = useMemo(() => ({ openContactsDrawer }), [openContactsDrawer]);

  const handleClose = (didChange?: boolean) => {
    setOpenTab(null);
    if (didChange && quote?.id) {
      evaluateQuoteRules(quote.id);
    }
  };

  return (
    <QuoteContactsDrawerContext.Provider value={value}>
      {children}

      {openTab && quote?.contacts && (
        <QuoteContactsDrawer
          initialTab={openTab}
          onClose={handleClose}
          updateQuoteSigningOrder={updateQuoteSigningOrder}
        />
      )}
    </QuoteContactsDrawerContext.Provider>
  );
};
